import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, FileText, DollarSign, TrendingUp, FolderKanban, ExternalLink } from 'lucide-react';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import DetailPageLayout from '../../components/crm/DetailPageLayout';
import dealsService, { Deal } from '../../services/crm/dealsService';
import dealStagesService, { DealStage } from '../../services/crm/dealStagesService';
import quotesService, { Quote } from '../../services/crm/quotesService';
import NotesPanel from '../../components/crm/NotesPanel';
import { useCrmRoleStore, isManager } from '../../store/crmRole.store';

const DealDetail: React.FC = () => {
  const { dealId = '' } = useParams();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [stages, setStages] = useState<DealStage[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState<string>('');
  const [stageId, setStageId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [stageProbability, setStageProbability] = useState('');
  const [forecastCategory, setForecastCategory] = useState<'pipeline' | 'best' | 'commit' | 'closed' | ''>('');
  const [expectedCloseQuarter, setExpectedCloseQuarter] = useState('');
  const [arrAmount, setArrAmount] = useState('');
  const [projectedInvoiceDate, setProjectedInvoiceDate] = useState('');
  const { role } = useCrmRoleStore();
  const canEdit = isManager(role);

  useEffect(() => { document.title = 'CRM | Deal'; }, []);

  const load = useCallback(async () => {
    if (!activeOrgId || !dealId) return;
    setLoading(true); setError(null);
    try {
      const [stRes, dRes, qRes] = await Promise.all([
        dealStagesService.list(activeOrgId),
        dealsService.get(dealId, activeOrgId),
        quotesService.list(activeOrgId, dealId)
      ]);
      setStages(stRes.data || []);
      setQuotes(qRes.data || []);
      const d = dRes.data;
      if (!d) { setError('Deal not found'); return; }
      setDeal(d);
      setTitle(d.title || '');
      setAmount(d.amount != null ? String(d.amount) : '');
      setStageId(d.stage_id || '');
      setCompanyId((d.company_id as string) || '');
      setCloseDate(d.close_date || '');
      setStageProbability(
        d.stage_probability != null && !Number.isNaN(Number(d.stage_probability))
          ? String(d.stage_probability)
          : ''
      );
      setForecastCategory((d.forecast_category as ('pipeline' | 'best' | 'commit' | 'closed') | undefined) ?? '');
      setExpectedCloseQuarter(d.expected_close_quarter || '');
      setArrAmount(d.arr_amount != null ? String(d.arr_amount) : '');
      setProjectedInvoiceDate(d.projected_invoice_date || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load deal');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, dealId]);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(async () => {
    if (!activeOrgId || !dealId) return;
    setLoading(true); setError(null);
    try {
      const payload = {
        organization_id: activeOrgId,
        title,
        amount: amount ? Number(amount) : undefined,
        stage_id: stageId || undefined,
        company_id: companyId || undefined,
        close_date: closeDate || undefined,
        stage_probability: stageProbability ? Number(stageProbability) : undefined,
        forecast_category: forecastCategory || undefined,
        expected_close_quarter: expectedCloseQuarter || undefined,
        arr_amount: arrAmount ? Number(arrAmount) : undefined,
        projected_invoice_date: projectedInvoiceDate || undefined
      };
      const { data } = await dealsService.update(dealId, payload);
      setDeal(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to save deal');
    } finally {
      setLoading(false);
    }
  }, [
    activeOrgId,
    amount,
    arrAmount,
    closeDate,
    companyId,
    dealId,
    expectedCloseQuarter,
    forecastCategory,
    projectedInvoiceDate,
    stageId,
    stageProbability,
    title
  ]);

  const stageLabel = useMemo(() => {
    if (!deal) return '—';
    const stage = stages.find((item) => item.id === deal.stage_id);
    return stage?.name || '—';
  }, [deal, stages]);

  const weightedValue = useMemo(() => {
    if (!deal) return null;
    if (typeof deal.weighted_amount === 'number') return deal.weighted_amount;
    if (deal.amount != null && stageProbability) {
      const parsed = Number(stageProbability);
      if (!Number.isNaN(parsed)) {
        return (deal.amount * parsed) / 100;
      }
    }
    return null;
  }, [deal, stageProbability]);

  return (
    <DetailPageLayout
      title={deal?.title || 'Deal'}
      backLink="/crm#deals"
      backLabel="Back to Deals"
      actions={
        <button
          onClick={() => { void save(); }}
          disabled={loading || !canEdit}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
      }
    >

      <div className="bg-white p-4 rounded shadow space-y-4">
        {!activeOrgId && (
          <div className="text-sm text-gray-600">Select or create an organization above to begin.</div>
        )}
        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div>Loading…</div>}

        {deal && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h2 className="font-medium">Overview</h2>
                <input className="border rounded p-2 w-full" placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} disabled={!canEdit} />
                <input className="border rounded p-2 w-full" placeholder="Amount (optional)" value={amount} onChange={e => setAmount(e.target.value)} disabled={!canEdit} />
                <select className="border rounded p-2 w-full" value={stageId} onChange={e => setStageId(e.target.value)} disabled={!canEdit}>
                  <option value="">Select Stage</option>
                  {stages.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
                <input className="border rounded p-2 w-full" placeholder="Company ID (optional)" value={companyId} onChange={e => setCompanyId(e.target.value)} disabled={!canEdit} />
                <input className="border rounded p-2 w-full" type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} disabled={!canEdit} />
              </div>
              <div className="space-y-2">
                <h2 className="font-medium">Forecasting</h2>
                <input
                  className="border rounded p-2 w-full"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="Stage probability (%)"
                  value={stageProbability}
                  onChange={(event) => setStageProbability(event.target.value)}
                  disabled={!canEdit}
                />
                <select
                  className="border rounded p-2 w-full"
                  value={forecastCategory}
                  onChange={(event) => setForecastCategory(event.target.value as typeof forecastCategory)}
                  disabled={!canEdit}
                >
                  <option value="">Forecast category…</option>
                  <option value="pipeline">Pipeline</option>
                  <option value="best">Best</option>
                  <option value="commit">Commit</option>
                  <option value="closed">Closed</option>
                </select>
                <input
                  className="border rounded p-2 w-full"
                  placeholder="Expected close quarter (e.g. 2025-Q1)"
                  value={expectedCloseQuarter}
                  onChange={(event) => setExpectedCloseQuarter(event.target.value)}
                  disabled={!canEdit}
                />
                <input
                  className="border rounded p-2 w-full"
                  placeholder="ARR amount (optional)"
                  value={arrAmount}
                  onChange={(event) => setArrAmount(event.target.value)}
                  disabled={!canEdit}
                />
                <input
                  className="border rounded p-2 w-full"
                  type="date"
                  value={projectedInvoiceDate}
                  onChange={(event) => setProjectedInvoiceDate(event.target.value)}
                  disabled={!canEdit}
                />
              </div>
            </div>
            {canEdit && (
              <div>
                <button className="bg-blue-600 text-white rounded px-4" onClick={save} disabled={loading}>Save</button>
              </div>
            )}

            {/* Quotes Section */}
            <div className="pt-6 border-t">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Quotes ({quotes.length})
                </h2>
                <button
                  onClick={() => navigate(`/crm/deals/${dealId}/quotes/new`)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <Plus className="w-4 h-4" />
                  Create Quote
                </button>
              </div>

              {quotes.length === 0 ? (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">No quotes yet</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Create a quote to show products, pricing, and profit margins
                  </p>
                  <button
                    onClick={() => navigate(`/crm/deals/${dealId}/quotes/new`)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    <Plus className="w-4 h-4" />
                    Create First Quote
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {quotes.map((quote) => (
                    <div
                      key={quote.id}
                      onClick={() => navigate(`/crm/deals/${dealId}/quotes/${quote.id}`)}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-gray-900">Quote #{quote.quote_number}</h3>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded ${
                                quote.status === 'accepted'
                                  ? 'bg-green-100 text-green-700'
                                  : quote.status === 'sent'
                                  ? 'bg-blue-100 text-blue-700'
                                  : quote.status === 'rejected'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {quote.status === 'accepted' && '✅ Accepted'}
                              {quote.status === 'sent' && '📤 Sent'}
                              {quote.status === 'rejected' && '❌ Rejected'}
                              {quote.status === 'draft' && '📝 Draft'}
                              {quote.status === 'expired' && '⏰ Expired'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500">Total Amount</div>
                              <div className="font-semibold text-gray-900 flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                ${quote.total_amount?.toFixed(2) || '0.00'}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500">Profit</div>
                              <div className="font-semibold text-green-600 flex items-center gap-1">
                                <TrendingUp className="w-4 h-4" />
                                ${quote.profit_amount?.toFixed(2) || '0.00'}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-500">Margin</div>
                              <div
                                className={`font-semibold ${
                                  (quote.profit_margin_percent || 0) < 20
                                    ? 'text-red-600'
                                    : (quote.profit_margin_percent || 0) < 40
                                    ? 'text-yellow-600'
                                    : 'text-green-600'
                                }`}
                              >
                                {quote.profit_margin_percent?.toFixed(1) || '0.0'}%
                              </div>
                            </div>
                          </div>

                          {quote.valid_until && (
                            <div className="mt-2 text-xs text-gray-500">
                              Valid until: {new Date(quote.valid_until).toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        <div className="text-right text-xs text-gray-500">
                          {quote.created_at && new Date(quote.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Project Management Link */}
            {deal?.pm_project_id && (
              <div className="pt-6 border-t">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FolderKanban className="w-6 h-6 text-green-600" />
                        <h3 className="text-lg font-semibold text-green-900">
                          Project Created! 🎉
                        </h3>
                      </div>
                      <p className="text-sm text-green-700 mb-3">
                        This deal has been converted into a project in the Project Management system.
                        The team can now track tasks, time, and delivery progress.
                      </p>
                      <button
                        onClick={() => navigate(`/project-management?project=${deal.pm_project_id}`)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                      >
                        <FolderKanban className="w-4 h-4" />
                        Open Project
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="text-xs text-green-600 font-medium mb-1">Auto-created from CRM</div>
                      <div className="text-xs text-green-700">
                        Tasks • Timeline • Budget
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-6 border-t">
              <NotesPanel entityType="deal" entityId={dealId} title="Deal Notes" />
            </div>
          </>
        )}
      </div>
    </DetailPageLayout>
  );
};

export default DealDetail;
